/**
 * Club calendar — NZ public holidays, regional anniversary days, and
 * awareness / campaign days a swim club can theme sessions around.
 *
 * Dates are NZ civil dates (YYYY-MM-DD). Holiday rules follow the
 * Holidays Act mondayisation used by Employment NZ / govt.nz.
 */

import { addDaysIso, dayOfWeekIso } from './time';
import { formatKupuWithGloss, TE_WIKI_SWIM_KUPU } from './teWikiKupu';

export type ObservanceKind =
  | 'public_holiday'
  | 'regional_holiday'
  | 'awareness_day'
  | 'local_event'
  | 'club_theme';

export type CalendarEventStatus = 'suggested' | 'adopted' | 'dismissed';

export type CalendarEventSource = 'nz_holidays' | 'curated' | 'ai_discovery' | 'manual';

export type MemberNotificationAudience = 'parent' | 'coach' | 'all';

export type DateRule =
  | { type: 'fixed'; month: number; day: number }
  | { type: 'nth_weekday'; month: number; weekday: number; nth: number }
  | { type: 'easter_offset'; offset: number }
  | { type: 'holiday'; id: HolidayId }
  | { type: 'year_table'; dates: Record<number, { start: string; end?: string }> };

export type HolidayId =
  | 'new_years_day'
  | 'day_after_new_years'
  | 'waitangi_day'
  | 'northland_anniversary'
  | 'good_friday'
  | 'easter_monday'
  | 'anzac_day'
  | 'kings_birthday'
  | 'matariki'
  | 'labour_day'
  | 'christmas_day'
  | 'boxing_day';

export interface ObservanceTemplate {
  slug: string;
  title: string;
  kind: ObservanceKind;
  rule: DateRule;
  description: string;
  sessionIdea: string;
  coachReminder: string;
  parentMessage: string;
  themeColor: string;
  dressCode: string | null;
  reminderLeadDays: number;
  notifyParents: boolean;
  notifyCoaches: boolean;
  cancelsTraining: boolean;
  source: CalendarEventSource;
  sourceUrl: string | null;
  region: 'nz' | 'northland' | 'whangarei';
}

export interface CalendarOccurrence {
  slug: string;
  title: string;
  kind: ObservanceKind;
  occursOn: string;
  endsOn: string | null;
  actualOn: string | null;
  description: string;
  sessionIdea: string;
  coachReminder: string;
  parentMessage: string;
  themeColor: string;
  dressCode: string | null;
  reminderLeadDays: number;
  notifyParents: boolean;
  notifyCoaches: boolean;
  cancelsTraining: boolean;
  source: CalendarEventSource;
  sourceUrl: string | null;
  region: 'nz' | 'northland' | 'whangarei';
}

export interface DeckObservance {
  id: string;
  slug: string;
  title: string;
  kind: ObservanceKind;
  status: CalendarEventStatus;
  occursOn: string;
  endsOn: string | null;
  description: string | null;
  sessionIdea: string | null;
  coachReminder: string | null;
  parentMessage: string | null;
  themeColor: string | null;
  dressCode: string | null;
  reminderLeadDays: number;
  cancelsTraining: boolean;
  source: string;
  href: string;
}

export interface MemberNotification {
  id: string;
  audience: MemberNotificationAudience;
  kind: string;
  title: string;
  body: string | null;
  href: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
}

/** Official Matariki public-holiday dates (Te Kāhui o Matariki / gazetted). */
export const MATARIKI_DATES: Record<number, string> = {
  2025: '2025-06-20',
  2026: '2026-07-10',
  2027: '2027-06-25',
  2028: '2028-07-14',
  2029: '2029-07-06',
  2030: '2030-06-21',
  2031: '2031-07-11',
  2032: '2032-07-09',
};

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function isoDate(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Shift Sat → Mon, Sun → Mon. Weekdays unchanged. */
export function mondayise(date: string): string {
  const dow = dayOfWeekIso(date);
  if (dow === 6) return addDaysIso(date, 2);
  if (dow === 0) return addDaysIso(date, 1);
  return date;
}

/**
 * If two mondayised dates collide (e.g. New Year's pair, Christmas/Boxing),
 * push the second to the next weekday.
 */
export function mondayisePair(first: string, second: string): { first: string; second: string } {
  const a = mondayise(first);
  let b = mondayise(second);
  if (b === a) b = addDaysIso(a, 1);
  return { first: a, second: b };
}

/** Meeus/Jones/Butcher Gregorian algorithm → Easter Sunday. */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDate(year, month, day);
}

/** nth weekday of a month. weekday: 0=Sun..6=Sat. nth=1 first, -1 last. */
export function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): string {
  if (nth < 0) {
    const lastDay = isoDate(year, month + 1, 1);
    const last = addDaysIso(lastDay, -1);
    const lastDow = dayOfWeekIso(last);
    const delta = (lastDow - weekday + 7) % 7;
    return addDaysIso(last, -delta);
  }
  const first = isoDate(year, month, 1);
  const firstDow = dayOfWeekIso(first);
  const delta = (weekday - firstDow + 7) % 7;
  return addDaysIso(first, delta + (nth - 1) * 7);
}

/** Monday closest to 29 January (Auckland / Northland Anniversary). */
export function mondayClosestToJan29(year: number): string {
  const target = isoDate(year, 1, 29);
  const dow = dayOfWeekIso(target);
  if (dow === 1) return target;
  const daysToPrevMon = (dow + 6) % 7;
  const daysToNextMon = (8 - dow) % 7;
  return daysToPrevMon <= daysToNextMon
    ? addDaysIso(target, -daysToPrevMon)
    : addDaysIso(target, daysToNextMon);
}

export function observedHoliday(year: number, id: HolidayId): { occursOn: string; actualOn: string | null } {
  switch (id) {
    case 'new_years_day': {
      const actual = isoDate(year, 1, 1);
      const pair = mondayisePair(actual, isoDate(year, 1, 2));
      return { occursOn: pair.first, actualOn: pair.first === actual ? null : actual };
    }
    case 'day_after_new_years': {
      const actual = isoDate(year, 1, 2);
      const pair = mondayisePair(isoDate(year, 1, 1), actual);
      return { occursOn: pair.second, actualOn: pair.second === actual ? null : actual };
    }
    case 'waitangi_day': {
      const actual = isoDate(year, 2, 6);
      const observed = mondayise(actual);
      return { occursOn: observed, actualOn: observed === actual ? null : actual };
    }
    case 'northland_anniversary':
      return { occursOn: mondayClosestToJan29(year), actualOn: isoDate(year, 1, 29) };
    case 'good_friday':
      return { occursOn: addDaysIso(easterSunday(year), -2), actualOn: null };
    case 'easter_monday':
      return { occursOn: addDaysIso(easterSunday(year), 1), actualOn: null };
    case 'anzac_day': {
      const actual = isoDate(year, 4, 25);
      const observed = mondayise(actual);
      return { occursOn: observed, actualOn: observed === actual ? null : actual };
    }
    case 'kings_birthday':
      return { occursOn: nthWeekdayOfMonth(year, 6, 1, 1), actualOn: null };
    case 'matariki': {
      const occursOn = MATARIKI_DATES[year];
      if (!occursOn) throw new Error(`No gazetted Matariki date for ${year}`);
      return { occursOn, actualOn: null };
    }
    case 'labour_day':
      return { occursOn: nthWeekdayOfMonth(year, 10, 1, 4), actualOn: null };
    case 'christmas_day': {
      const actual = isoDate(year, 12, 25);
      const pair = mondayisePair(actual, isoDate(year, 12, 26));
      return { occursOn: pair.first, actualOn: pair.first === actual ? null : actual };
    }
    case 'boxing_day': {
      const actual = isoDate(year, 12, 26);
      const pair = mondayisePair(isoDate(year, 12, 25), actual);
      return { occursOn: pair.second, actualOn: pair.second === actual ? null : actual };
    }
    default:
      throw new Error(`Unknown holiday id: ${id}`);
  }
}

function resolveRule(year: number, rule: DateRule): { occursOn: string; endsOn: string | null; actualOn: string | null } | null {
  switch (rule.type) {
    case 'fixed':
      return { occursOn: isoDate(year, rule.month, rule.day), endsOn: null, actualOn: null };
    case 'nth_weekday':
      return {
        occursOn: nthWeekdayOfMonth(year, rule.month, rule.weekday, rule.nth),
        endsOn: null,
        actualOn: null,
      };
    case 'easter_offset':
      return { occursOn: addDaysIso(easterSunday(year), rule.offset), endsOn: null, actualOn: null };
    case 'holiday':
      try {
        const h = observedHoliday(year, rule.id);
        return { occursOn: h.occursOn, endsOn: null, actualOn: h.actualOn };
      } catch {
        return null;
      }
    case 'year_table': {
      const row = rule.dates[year];
      if (!row) return null;
      return { occursOn: row.start, endsOn: row.end ?? null, actualOn: null };
    }
    default:
      return null;
  }
}

/**
 * Ministry of Education national term breaks for state and state-integrated
 * schools and kura. Dates are the published holiday ranges on
 * https://www.education.govt.nz/school/school-terms-and-holidays
 * (page updated 21 May 2026; 2025 is the past-years section on that page).
 *
 * Term 1–3 use the published start and end inclusive.
 * Summer starts on the published "no later than" date (every school has
 * closed) and ends the day before the earliest published Term 1 start of
 * the next year (every school is still closed). 2028 summer is omitted:
 * the 2029 Term 1 window is not published.
 *
 * Stored as awareness_day because observance_kind has no school_holiday
 * value and this ship must not add a migration. The slug is the discriminator.
 */
export const NZ_SCHOOL_HOLIDAY_SOURCE_URL =
  'https://www.education.govt.nz/school/school-terms-and-holidays';

export type NzSchoolHolidayBreak = 'term-1' | 'term-2' | 'term-3' | 'summer';

export const NZ_SCHOOL_HOLIDAY_DATES: Record<
  NzSchoolHolidayBreak,
  Record<number, { start: string; end: string }>
> = {
  'term-1': {
    2025: { start: '2025-04-12', end: '2025-04-27' },
    2026: { start: '2026-04-03', end: '2026-04-19' },
    2027: { start: '2027-04-10', end: '2027-04-26' },
    2028: { start: '2028-04-14', end: '2028-04-30' },
  },
  'term-2': {
    2025: { start: '2025-06-28', end: '2025-07-13' },
    2026: { start: '2026-07-04', end: '2026-07-19' },
    2027: { start: '2027-07-03', end: '2027-07-18' },
    2028: { start: '2028-07-08', end: '2028-07-23' },
  },
  'term-3': {
    2025: { start: '2025-09-20', end: '2025-10-05' },
    2026: { start: '2026-09-26', end: '2026-10-11' },
    2027: { start: '2027-09-25', end: '2027-10-10' },
    2028: { start: '2028-09-30', end: '2028-10-15' },
  },
  summer: {
    2025: { start: '2025-12-20', end: '2026-01-25' },
    2026: { start: '2026-12-19', end: '2027-01-27' },
    2027: { start: '2027-12-18', end: '2028-01-30' },
  },
};

export function nzSchoolHolidaySlug(breakId: NzSchoolHolidayBreak): string {
  return `nz-school-holiday-${breakId}`;
}

export function isNzSchoolHolidaySlug(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && slug.startsWith('nz-school-holiday-');
}

const NZ_SCHOOL_HOLIDAY_COPY: Record<NzSchoolHolidayBreak, { title: string; description: string }> = {
  'term-1': {
    title: 'NZ school holidays (Term 1)',
    description:
      'Ministry of Education national term break after Term 1, for state and state-integrated schools and kura. Club training still runs unless the club posts a change.',
  },
  'term-2': {
    title: 'NZ school holidays (Term 2)',
    description:
      'Ministry of Education national term break after Term 2, for state and state-integrated schools and kura. Club training still runs unless the club posts a change.',
  },
  'term-3': {
    title: 'NZ school holidays (Term 3)',
    description:
      'Ministry of Education national term break after Term 3, for state and state-integrated schools and kura. Club training still runs unless the club posts a change.',
  },
  summer: {
    title: 'NZ school holidays (summer)',
    description:
      'Ministry of Education summer holidays. The range starts on the published "no later than" date, when every state school has closed, and ends the day before the earliest published Term 1 start next year, when every state school is still closed. Some schools close earlier and some reopen later inside the published window. Club training still runs unless the club posts a change.',
  },
};

function nzSchoolHolidayTemplate(breakId: NzSchoolHolidayBreak): ObservanceTemplate {
  const copy = NZ_SCHOOL_HOLIDAY_COPY[breakId];
  return {
    slug: nzSchoolHolidaySlug(breakId),
    title: copy.title,
    kind: 'awareness_day',
    rule: { type: 'year_table', dates: NZ_SCHOOL_HOLIDAY_DATES[breakId] },
    description: copy.description,
    sessionIdea: 'Keep the normal set. Expect some absences — skip test sets while families are away.',
    coachReminder: 'School holidays. Training still runs unless you have posted a change. Check who is away before you set lanes.',
    parentMessage: 'School holidays are on. Club training still runs unless the club posts a change.',
    themeColor: '#0369a1',
    dressCode: null,
    reminderLeadDays: 7,
    notifyParents: false,
    notifyCoaches: false,
    cancelsTraining: false,
    source: 'nz_holidays',
    sourceUrl: NZ_SCHOOL_HOLIDAY_SOURCE_URL,
    region: 'nz',
  };
}

const NZ_SCHOOL_HOLIDAY_TEMPLATES: ObservanceTemplate[] = (
  ['term-1', 'term-2', 'term-3', 'summer'] as const
).map(nzSchoolHolidayTemplate);

export const OBSERVANCE_CATALOG: ObservanceTemplate[] = [
  {
    slug: 'new-years-day',
    title: "New Year's Day",
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'new_years_day' },
    description: 'National public holiday. Most pools and the Aquatic Centre are closed.',
    sessionIdea: 'No training — rest week kick-off. Share a “welcome back” note for the first session after the break.',
    coachReminder: 'Confirm the first training day of the year and post it for parents.',
    parentMessage: "Happy New Year from WASC. There's no training on New Year's Day — see you at the first session back.",
    themeColor: '#0f766e',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'day-after-new-years',
    title: "Day after New Year's Day",
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'day_after_new_years' },
    description: 'National public holiday (2 January, mondayised when it falls on a weekend).',
    sessionIdea: 'Still a holiday — keep the rest-week message consistent with New Year’s Day.',
    coachReminder: 'No sessions today. Double-check the first Monday back is materialised.',
    parentMessage: 'No training today — the pool is closed for the New Year holiday.',
    themeColor: '#0f766e',
    dressCode: null,
    reminderLeadDays: 2,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'northland-anniversary',
    title: 'Northland Anniversary Day',
    kind: 'regional_holiday',
    rule: { type: 'holiday', id: 'northland_anniversary' },
    description: 'Northland observes Auckland Anniversary — the Monday closest to 29 January.',
    sessionIdea: 'Holiday Monday — no training. Use the week before for a “Northland pride” fun set if you want a local hook.',
    coachReminder: 'Regional holiday — sessions are typically cancelled. Confirm the Aquatic Centre hours.',
    parentMessage: 'Northland Anniversary Day — no club training. Enjoy the long weekend.',
    themeColor: '#1d4ed8',
    dressCode: null,
    reminderLeadDays: 4,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.employment.govt.nz/leave-and-holidays/public-holidays/public-holidays-and-anniversary-dates',
    region: 'northland',
  },
  {
    slug: 'waitangi-day',
    title: 'Waitangi Day',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'waitangi_day' },
    description: 'National public holiday. When 6 February falls on a weekend it is observed on the Monday.',
    sessionIdea: 'No training on the observed holiday. The week of Waitangi Day is a good moment for a te reo warm-up and a kōrero about the whenua we train on.',
    coachReminder: 'Holiday — no sessions. If you run a session the day before, open with a short Waitangi kōrero.',
    parentMessage: 'Kia ora — no training on Waitangi Day. See you at the next scheduled session.',
    themeColor: '#166534',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'good-friday',
    title: 'Good Friday',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'good_friday' },
    description: 'Easter Friday — national public holiday. Training does not run.',
    sessionIdea: 'Easter weekend rest. Post the Tuesday return so families can plan camps and travel.',
    coachReminder: 'Good Friday — no sessions. Confirm Easter Monday is also cancelled.',
    parentMessage: 'No training on Good Friday. Enjoy the long weekend — we are back after Easter Monday.',
    themeColor: '#7c3aed',
    dressCode: null,
    reminderLeadDays: 4,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'easter-monday',
    title: 'Easter Monday',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'easter_monday' },
    description: 'National public holiday. Club training does not run.',
    sessionIdea: 'Rest day. First session back is a good “shake-out” aerobic set, not a tester.',
    coachReminder: 'Easter Monday — no sessions.',
    parentMessage: 'No training on Easter Monday. See you at the next weekday session.',
    themeColor: '#7c3aed',
    dressCode: null,
    reminderLeadDays: 2,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'anzac-day',
    title: 'ANZAC Day',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'anzac_day' },
    description: '25 April. When it falls on a weekend it is observed on the Monday.',
    sessionIdea: 'No training on the observed holiday. If you train the day before, keep the tone respectful — no themed dress-ups.',
    coachReminder: 'ANZAC Day holiday — sessions cancelled. Dawn services run locally in Whangārei.',
    parentMessage: 'No club training on ANZAC Day. Lest we forget.',
    themeColor: '#44403c',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'kings-birthday',
    title: "King's Birthday",
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'kings_birthday' },
    description: 'First Monday in June — national public holiday.',
    sessionIdea: 'Long weekend rest. First session back: technique emphasis, not a big aerobic dump.',
    coachReminder: "King's Birthday Monday — no sessions.",
    parentMessage: "No training on King's Birthday Monday. Enjoy the long weekend.",
    themeColor: '#1e3a8a',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'matariki',
    title: 'Matariki',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'matariki' },
    description: 'Matariki public holiday — the Māori new year. Gazetted date each year.',
    sessionIdea: 'Holiday — no training. The week of Matariki is a beautiful time for a “new year, new goals” kick-off at the first session back: one goal per swimmer, spoken on the wall.',
    coachReminder: 'Matariki holiday — no sessions. Consider a Matariki-themed first session back.',
    parentMessage: 'Ngā mihi o te tau hou — no training on Matariki. See you at the next session.',
    themeColor: '#1e1b4b',
    dressCode: null,
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'labour-day',
    title: 'Labour Day',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'labour_day' },
    description: 'Fourth Monday in October — national public holiday.',
    sessionIdea: 'Long weekend rest. Good week to thank parent volunteers on deck.',
    coachReminder: 'Labour Day Monday — no sessions.',
    parentMessage: 'No training on Labour Day. See you Tuesday.',
    themeColor: '#9a3412',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'christmas-day',
    title: 'Christmas Day',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'christmas_day' },
    description: '25 December, mondayised when it falls on a weekend.',
    sessionIdea: 'Club is off. Share the first session date of the new year now so families can plan.',
    coachReminder: 'Christmas Day — no sessions. Confirm the Boxing Day / New Year shutdown window.',
    parentMessage: 'Merry Christmas from WASC. No training today — enjoy the break with whānau.',
    themeColor: '#b91c1c',
    dressCode: null,
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },
  {
    slug: 'boxing-day',
    title: 'Boxing Day',
    kind: 'public_holiday',
    rule: { type: 'holiday', id: 'boxing_day' },
    description: '26 December, mondayised (or Tuesday when it collides with a mondayised Christmas).',
    sessionIdea: 'Still the Christmas shutdown. Post the return-to-training date.',
    coachReminder: 'Boxing Day holiday — no sessions.',
    parentMessage: 'No training on Boxing Day. Enjoy the break.',
    themeColor: '#b91c1c',
    dressCode: null,
    reminderLeadDays: 2,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: true,
    source: 'nz_holidays',
    sourceUrl: 'https://www.govt.nz/browse/work-and-tax/public-holidays-and-work/dates-for-public-holidays-and-anniversary-days/',
    region: 'nz',
  },

  ...NZ_SCHOOL_HOLIDAY_TEMPLATES,

  // ── Awareness / campaign days a swim club can get behind ──
  {
    slug: 'childrens-day-nz',
    title: "Children's Day",
    kind: 'awareness_day',
    rule: { type: 'nth_weekday', month: 3, weekday: 0, nth: 1 },
    description: 'First Sunday of March — Aotearoa Children’s Day. Families first.',
    sessionIdea: 'Sunday is rest, but the Friday before: parent-on-deck relays, silly finishes, and a “bring a sibling to watch” invite.',
    coachReminder: 'Children’s Day weekend — keep Friday’s session playful. Extra high-fives, extra games.',
    parentMessage: 'Children’s Day this weekend — Friday’s session is a fun one. Families welcome on the balcony.',
    themeColor: '#f59e0b',
    dressCode: null,
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.childrensday.org.nz/',
    region: 'nz',
  },
  {
    slug: 'international-womens-day',
    title: "International Women's Day",
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 3, day: 8 },
    description: '8 March — celebrate wāhine on deck: coaches, officials, swimmers, and the parents who keep the club running.',
    sessionIdea: 'Name a set after a WASC woman coach or official. Junior/Fitness: “power of three” relay (three girls lead off).',
    coachReminder: 'IWD — shout out the wāhine on your squad. A 30-second kōrero at land activation is enough.',
    parentMessage: "International Women's Day — come watch, and thank the women who coach and volunteer at WASC.",
    themeColor: '#db2777',
    dressCode: 'Optional purple or IWD colours',
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.internationalwomensday.com/',
    region: 'nz',
  },
  {
    slug: 'world-water-day',
    title: 'World Water Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 3, day: 22 },
    description: '22 March — UN World Water Day. Perfect hook for a swim club.',
    sessionIdea: '“Treasure the water” theme: streamline contests, no-waste-of-a-length challenge, and a 2-minute water-safety chat on the wall.',
    coachReminder: 'World Water Day — weave a water-safety cue into land activation. One rule, said twice.',
    parentMessage: 'World Water Day — we’re talking water safety at training. Ask your swimmer what the cue was.',
    themeColor: '#0284c7',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.un.org/en/observances/water-day',
    region: 'nz',
  },
  {
    slug: 'pink-shirt-day',
    title: 'Pink Shirt Day',
    kind: 'awareness_day',
    rule: { type: 'nth_weekday', month: 5, weekday: 5, nth: 3 },
    description: 'Third Friday of May — Mental Health Foundation campaign against bullying. Wear pink and be an upstander.',
    sessionIdea: 'Everyone in pink caps/shirts on land. Kindness relay: a swimmer can only dive when a teammate has cheered them by name. Junior: pink-float treasure hunt.',
    coachReminder: 'Wear pink this week — especially Friday. Call out kindness on deck. No roasting, extra names-used.',
    parentMessage: 'Pink Shirt Day Friday — please send your swimmer in pink. We’re talking kindness and being an upstander at training.',
    themeColor: '#ec4899',
    dressCode: 'Wear pink',
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://pinkshirtday.org.nz/',
    region: 'nz',
  },
  {
    slug: 'idahobit',
    title: 'IDAHOBIT',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 5, day: 17 },
    description: '17 May — International Day Against Homophobia, Biphobia and Transphobia. Rainbow inclusion on deck.',
    sessionIdea: 'Rainbow lane markers / caps if you have them. One rule: every swimmer is greeted by name. Keep it warm, not a lecture.',
    coachReminder: 'IDAHOBIT — rainbow welcome on deck. Check the language you use in the huddle.',
    parentMessage: 'This Sunday/Monday we mark IDAHOBIT — everyone belongs at WASC. Training as usual.',
    themeColor: '#a21caf',
    dressCode: 'Optional rainbow',
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.idahobit.org.au/',
    region: 'nz',
  },
  {
    slug: 'world-oceans-day',
    title: 'World Oceans Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 6, day: 8 },
    description: '8 June — a natural fit for a Northland swim club that trains beside the harbour.',
    sessionIdea: 'Ocean-themed fun: “reef to beach” descending 50s, dolphin-kick challenge, and a 60-second chat about harbour / surf safety.',
    coachReminder: 'World Oceans Day — ocean-themed set. One water-safety cue for open water vs pool.',
    parentMessage: 'World Oceans Day — we’re running an ocean-themed session. Ask your swimmer about the harbour-safety cue.',
    themeColor: '#0e7490',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://worldoceansday.org/',
    region: 'nz',
  },
  {
    slug: 'olympic-day',
    title: 'Olympic Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 6, day: 23 },
    description: '23 June — Olympic Day. Dream big, race fair.',
    sessionIdea: 'Mini-meet inside the session: 25s or 50s, official starts, cheers for every lane. Fitness: “Olympic stations” (kick / pull / swim).',
    coachReminder: 'Olympic Day — run a mini-meet flavour. Medals optional, cheers mandatory.',
    parentMessage: 'Olympic Day at training — a bit more race energy than usual. Come watch if you can.',
    themeColor: '#1d4ed8',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.olympics.com/ioc/olympic-day',
    region: 'nz',
  },
  {
    slug: 'world-drowning-prevention-day',
    title: 'World Drowning Prevention Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 7, day: 25 },
    description: '25 July — UN / Water Safety NZ. The most important day on a swim club calendar.',
    sessionIdea: 'Every squad: 5-minute water-safety block (float, call for help, buddy). Junior: clothes-float demo if the facility allows. Hand out the Water Safety NZ “safer swimming” reminder.',
    coachReminder: 'World Drowning Prevention Day — 5 minutes on the wall for float / call / buddy. This is the day, not a nice-to-have.',
    parentMessage: 'World Drowning Prevention Day — we’re teaching float, call for help, and swim with a buddy. Ask your swimmer to show you the float at home (in the lounge is fine).',
    themeColor: '#0369a1',
    dressCode: null,
    reminderLeadDays: 4,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.watersafety.org.nz/',
    region: 'nz',
  },
  {
    slug: 'daffodil-day',
    title: 'Daffodil Day',
    kind: 'awareness_day',
    rule: { type: 'nth_weekday', month: 8, weekday: 5, nth: -1 },
    description: 'Last Friday of August — Cancer Society street appeal. Wear yellow.',
    sessionIdea: 'Yellow caps / shirts on land. Optional gold-coin koha bucket for the Cancer Society. Keep the set normal; the colour does the talking.',
    coachReminder: 'Wear yellow this Friday for Daffodil Day. Optional koha bucket on the table.',
    parentMessage: 'Daffodil Day Friday — yellow if you have it. Optional gold-coin koha for the Cancer Society at the pool door.',
    themeColor: '#eab308',
    dressCode: 'Wear yellow',
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.daffodilday.org.nz/',
    region: 'nz',
  },
  {
    slug: 'wear-it-purple-day',
    title: 'Wear it Purple Day',
    kind: 'awareness_day',
    rule: { type: 'nth_weekday', month: 8, weekday: 5, nth: -1 },
    description: 'Late August rainbow/inclusion day for young people. 2026 falls on 28 August (same Friday as Daffodil Day — pick a colour story).',
    sessionIdea: 'If you also mark Daffodil Day, do yellow in the water and a purple hair-tie / cap accent. Message: everyone belongs in this lane.',
    coachReminder: 'Wear it Purple / Daffodil overlap — yellow + a purple accent works. Inclusion language in the huddle.',
    parentMessage: 'This Friday we are in club colours of kindness — yellow for Daffodil Day, purple welcome too.',
    themeColor: '#7e22ce',
    dressCode: 'Wear purple (or yellow + purple)',
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.wearitpurple.org/',
    region: 'nz',
  },
  {
    slug: 'te-wiki-o-te-reo-maori',
    title: 'Te Wiki o te Reo Māori',
    kind: 'awareness_day',
    rule: {
      type: 'year_table',
      dates: {
        2025: { start: '2025-09-14', end: '2025-09-20' },
        2026: { start: '2026-09-14', end: '2026-09-20' },
        2027: { start: '2027-09-13', end: '2027-09-19' },
      },
    },
    description:
      `Te Wiki o te Reo Māori. 2026 kaupapa: Te Reo Towns — Ake, Ake, Ake. Use te reo with tohutō on deck: kia ora, kia kaha, ka rawe, plus swim kupu — ${formatKupuWithGloss(TE_WIKI_SWIM_KUPU)}.`,
    sessionIdea:
      `Wall kupu this week are swim reo: ${formatKupuWithGloss(TE_WIKI_SWIM_KUPU)}. Name one set in te reo each session (kikī, kume, ruku, omaoma ki te pito). Bilingual raina numbers. Kaiako greet every ākonga by name with kia ora / kia kaha / ka rawe.`,
    coachReminder:
      `Nau mai ki Te Wiki o te Reo Māori. Swim kupu on deck: ${formatKupuWithGloss(TE_WIKI_SWIM_KUPU)}. Greet every ākonga with kia ora. Tohutō matter — write Māori, not Maori.`,
    parentMessage:
      'Nau mai ki Te Wiki o te Reo Māori. At kaukau we are using ruku (dive), kikī (kick), kume (pull), raina (lane), tīmata (start), mutunga (finish) and tere (fast). Ask your tamariki to ako you one kupu. Whānau welcome on the balcony.',
    themeColor: '#15803d',
    dressCode: null,
    reminderLeadDays: 7,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://en.tetaurawhiri.govt.nz/te-wiki-2026',
    region: 'nz',
  },
  {
    slug: 'mental-health-awareness-week',
    title: 'Mental Health Awareness Week',
    kind: 'awareness_day',
    rule: {
      type: 'year_table',
      // Official dates from mhaw.nz. Do not invent later years until published.
      dates: {
        2026: { start: '2026-10-12', end: '2026-10-18' },
      },
    },
    description: 'Mental Health Foundation week, 12–18 October 2026. Theme: Belonging Matters. Check in, not just the clock.',
    sessionIdea: 'One “how’s your tank?” check-in per swimmer this week. Lighter Tuesday, fun Friday. No public calling-out of times if a swimmer is having a rough day.',
    coachReminder: 'MHAW — check in with quiet swimmers. Fun Friday. If someone looks off, a private word beats a louder set.',
    parentMessage: 'Mental Health Awareness Week — training has a bit more play in it. If home is heavy, tell the coach; we will look after your swimmer.',
    themeColor: '#0d9488',
    dressCode: null,
    reminderLeadDays: 7,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://mhaw.nz/',
    region: 'nz',
  },
  {
    slug: 'world-mental-health-day',
    title: 'World Mental Health Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 10, day: 10 },
    description: '10 October — WHO World Mental Health Day. In 2026 this is two days before Mental Health Awareness Week (12–18 October).',
    sessionIdea: 'Gratitude 25s: each repeat, a swimmer names something that went well today (can be “I made it to the wall”).',
    coachReminder: 'World Mental Health Day — keep the set kind. Name something that went well.',
    parentMessage: 'World Mental Health Day — a gentler session. Ask your swimmer what went well.',
    themeColor: '#0f766e',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.who.int/campaigns/world-mental-health-day',
    region: 'nz',
  },
  {
    slug: 'loud-shirt-day',
    title: 'Loud Shirt Day',
    kind: 'awareness_day',
    rule: {
      type: 'year_table',
      dates: {
        2026: { start: '2026-10-16' },
      },
    },
    description: 'Friday 16 October 2026 — wear your boldest shirt for The Hearing House / SCIP. Falls inside Mental Health Awareness Week this year.',
    sessionIdea: 'Loudest shirt wins a silly prize. On-deck: face the swimmers when you talk, one instruction at a time, no shouting from behind.',
    coachReminder: 'Loud Shirt Day — wear your loudest shirt. Face swimmers when you speak; one cue at a time.',
    parentMessage: 'Loud Shirt Day Friday — send them in the brightest shirt you own. We’re practising clearer communication on deck.',
    themeColor: '#ea580c',
    dressCode: 'Wear your loudest shirt',
    reminderLeadDays: 5,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.loudshirtday.org/',
    region: 'nz',
  },
  {
    slug: 'world-kindness-day',
    title: 'World Kindness Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 11, day: 13 },
    description: '13 November — a simple, coachable day.',
    sessionIdea: 'Buddy lanes: you cannot finish a 50 until you have cheered your lane-mate. Coaches model it first.',
    coachReminder: 'World Kindness Day — cheer every finish. Catch someone doing it right.',
    parentMessage: 'World Kindness Day at training — we’re practising cheering for the lane, not just the clock.',
    themeColor: '#f43f5e',
    dressCode: null,
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://www.kindness.org.nz/',
    region: 'nz',
  },
  {
    slug: 'white-ribbon-day',
    title: 'White Ribbon Day',
    kind: 'awareness_day',
    rule: { type: 'fixed', month: 11, day: 25 },
    description: '25 November — end violence against women. White ribbon if you have one.',
    sessionIdea: 'Keep the set normal. A short, age-appropriate “respect on deck and at home” line from the coach. Optional white ribbon on the lanyard.',
    coachReminder: 'White Ribbon Day — wear a white ribbon if you have one. Respect language on deck.',
    parentMessage: 'White Ribbon Day — we’re marking it quietly at training. Respect for everyone in the club.',
    themeColor: '#e7e5e4',
    dressCode: 'Optional white ribbon',
    reminderLeadDays: 3,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://whiteribbon.org.nz/',
    region: 'nz',
  },
  {
    slug: 'movember',
    title: 'Movember',
    kind: 'awareness_day',
    rule: { type: 'year_table', dates: { 2026: { start: '2026-11-01', end: '2026-11-30' }, 2027: { start: '2027-11-01', end: '2027-11-30' } } },
    description: 'November — men’s health (mental health, prostate, testicular). Optional mo, mandatory check-ins.',
    sessionIdea: 'One “how’s the tank?” Friday a month. Optional gold-coin koha. Keep it light for Juniors; more honest for Performance / Fitness adults.',
    coachReminder: 'Movember — men’s health check-in Fridays. You do not need a moustache to ask “you good?”.',
    parentMessage: 'Movember at WASC — a men’s health check-in month. Fitness and Performance will hear a bit more “you good?” from the coaches.',
    themeColor: '#292524',
    dressCode: 'Optional moustache / mo merch',
    reminderLeadDays: 7,
    notifyParents: true,
    notifyCoaches: true,
    cancelsTraining: false,
    source: 'curated',
    sourceUrl: 'https://nz.movember.com/',
    region: 'nz',
  },
];

export function occurrencesForYear(year: number, region: 'nz' | 'northland' | 'whangarei' = 'northland'): CalendarOccurrence[] {
  const out: CalendarOccurrence[] = [];
  for (const t of OBSERVANCE_CATALOG) {
    if (t.region === 'northland' && region === 'nz') continue;
    const resolved = resolveRule(year, t.rule);
    if (!resolved) continue;
    const extra =
      resolved.actualOn && resolved.actualOn !== resolved.occursOn
        ? ` Observed ${formatIsoHuman(resolved.occursOn)} (actual ${formatIsoHuman(resolved.actualOn)}).`
        : '';
    out.push({
      slug: t.slug,
      title: t.title,
      kind: t.kind,
      occursOn: resolved.occursOn,
      endsOn: resolved.endsOn,
      actualOn: resolved.actualOn,
      description: t.description + extra,
      sessionIdea: t.sessionIdea,
      coachReminder: t.coachReminder,
      parentMessage: t.parentMessage,
      themeColor: t.themeColor,
      dressCode: t.dressCode,
      reminderLeadDays: t.reminderLeadDays,
      notifyParents: t.notifyParents,
      notifyCoaches: t.notifyCoaches,
      cancelsTraining: t.cancelsTraining,
      source: t.source,
      sourceUrl: t.sourceUrl,
      region: t.region,
    });
  }
  return out.sort((a, b) => a.occursOn.localeCompare(b.occursOn));
}

export function occurrencesForYears(fromYear: number, toYear: number, region: 'nz' | 'northland' | 'whangarei' = 'northland'): CalendarOccurrence[] {
  const years: CalendarOccurrence[] = [];
  for (let y = fromYear; y <= toYear; y++) years.push(...occurrencesForYear(y, region));
  return years.sort((a, b) => a.occursOn.localeCompare(b.occursOn) || a.slug.localeCompare(b.slug));
}

export function occurrenceToDeckObservance(occ: CalendarOccurrence, href = '/app/calendar'): DeckObservance {
  return {
    id: `catalog:${occ.slug}:${occ.occursOn}`,
    slug: occ.slug,
    title: occ.title,
    kind: occ.kind,
    status: 'adopted',
    occursOn: occ.occursOn,
    endsOn: occ.endsOn,
    description: occ.description,
    sessionIdea: occ.sessionIdea,
    coachReminder: occ.coachReminder,
    parentMessage: occ.parentMessage,
    themeColor: occ.themeColor,
    dressCode: occ.dressCode,
    reminderLeadDays: occ.reminderLeadDays,
    cancelsTraining: occ.cancelsTraining,
    source: occ.source,
    href,
  };
}

/** Catalog days that overlap a date window — used when the DB seed is empty. */
export function catalogObservancesInWindow(
  from: string,
  to: string,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): DeckObservance[] {
  const y0 = parseInt(from.slice(0, 4), 10);
  const y1 = parseInt(to.slice(0, 4), 10);
  if (!Number.isFinite(y0) || !Number.isFinite(y1)) return [];
  // Summer holidays start in December and finish in January. A January
  // window has to see the previous year's catalog row.
  const startYear = y0 > 0 ? y0 - 1 : y0;
  return occurrencesForYears(startYear, Math.max(y0, y1), region)
    .filter((o) => eventOverlapsRange(o.occursOn, o.endsOn, from, to))
    .map((o) => occurrenceToDeckObservance(o));
}

export function eventWindowEnd(occursOn: string, endsOn: string | null | undefined): string {
  return endsOn && endsOn > occursOn ? endsOn : occursOn;
}

/** True the day after the event's last date. Today still counts as on. */
export function eventHasPassed(
  occursOn: string,
  endsOn: string | null | undefined,
  today: string,
): boolean {
  return eventWindowEnd(occursOn, endsOn) < today;
}

export function eventOverlapsRange(
  occursOn: string,
  endsOn: string | null | undefined,
  from: string,
  to: string,
): boolean {
  const end = eventWindowEnd(occursOn, endsOn);
  return occursOn <= to && end >= from;
}

export function eventIsActiveOn(occursOn: string, endsOn: string | null | undefined, today: string): boolean {
  return eventOverlapsRange(occursOn, endsOn, today, today);
}

export type ReminderPhase = 'week' | 'day' | null;

/**
 * week = inside the lead window before the day/week starts.
 * day  = the day itself, or any day inside a multi-day observance.
 */
export function reminderPhase(
  today: string,
  occursOn: string,
  endsOn: string | null | undefined,
  leadDays: number,
): ReminderPhase {
  if (eventIsActiveOn(occursOn, endsOn, today)) return 'day';
  const leadStart = addDaysIso(occursOn, -Math.max(0, leadDays));
  if (today >= leadStart && today < occursOn) return 'week';
  return null;
}

export function formatIsoHuman(iso: string): string {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function isoToUtcDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  return new Date(Date.UTC(y, m - 1, d));
}

function formatIsoShort(iso: string, includeMonth: boolean): string {
  const raw = isoToUtcDate(iso).toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: 'numeric',
    month: includeMonth ? 'short' : undefined,
    timeZone: 'UTC',
  });
  return raw.replace(/,/g, '');
}

/** Home / deck label: “Fri 15 May” or “Mon 14 – Sun 20 Sep”. */
export function formatObservanceWhen(occursOn: string, endsOn: string | null | undefined): string {
  const endIso = endsOn && endsOn > occursOn ? endsOn : null;
  if (!endIso) return formatIsoShort(occursOn, true);
  const start = isoToUtcDate(occursOn);
  const end = isoToUtcDate(endIso);
  const sameMonth =
    start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  return `${formatIsoShort(occursOn, !sameMonth)} – ${formatIsoShort(endIso, true)}`;
}

export function monthGrid(year: number, month: number): string[] {
  const first = isoDate(year, month, 1);
  const startPad = (dayOfWeekIso(first) + 6) % 7; // Monday-first
  const start = addDaysIso(first, -startPad);
  const cells: string[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDaysIso(start, i));
  return cells;
}

/** Parent and coach member_notifications copy for a calendar reminder. */
export function calendarReminderCopy(input: {
  title: string;
  occursOn: string;
  endsOn?: string | null;
  dressCode?: string | null;
  parentMessage?: string | null;
  coachReminder?: string | null;
  phase: 'week' | 'day';
  audience: MemberNotificationAudience;
}): { title: string; body: string } {
  const dress = input.dressCode ? ` ${input.dressCode}.` : '';
  const spans = Boolean(input.endsOn && input.endsOn !== input.occursOn);
  if (input.phase === 'week') {
    const title = spans ? `${input.title} is this week` : `${input.title} is coming up`;
    const body =
      input.audience === 'coach'
        ? (input.coachReminder ?? `Prep the squad for ${input.title}.${dress}`)
        : (input.parentMessage ?? `${input.title} is this week.${dress}`);
    return { title, body };
  }
  const title = spans ? `${input.title} — on now` : `Today is ${input.title}`;
  const body =
    input.audience === 'coach'
      ? (input.coachReminder ?? `Theme today's session around ${input.title}.${dress}`)
      : (input.parentMessage ?? `Today is ${input.title}.${dress}`);
  return { title, body };
}

/** Reminder a catalog observance would send. Single-day rows use a null end date. */
export function catalogReminderCopy(
  slug: string,
  phase: 'week' | 'day',
  audience: MemberNotificationAudience = 'parent',
): { title: string; body: string } | null {
  const row = OBSERVANCE_CATALOG.find((item) => item.slug === slug);
  if (!row) return null;
  return calendarReminderCopy({
    title: row.title,
    occursOn: '2000-01-01',
    endsOn: null,
    dressCode: row.dressCode,
    parentMessage: row.parentMessage,
    coachReminder: row.coachReminder,
    phase,
    audience,
  });
}

export const CALENDAR_KIND_LABEL: Record<ObservanceKind, string> = {
  public_holiday: 'Public holiday',
  regional_holiday: 'Regional holiday',
  awareness_day: 'Awareness day',
  local_event: 'Local',
  club_theme: 'Club theme',
};
